// src/components/ModalBox/ModalBox.styles.ts
//
// Styling for the generic window-style dialog. Nothing here is aware of what
// the caller renders inside it: no form selectors, no Query terminology, no
// assumptions about the content's height beyond "it may be taller than the
// window, in which case the body scrolls".
//
// Structure and why:
//
//   Root   fixed layer + flex centring. NOT animated, so the window can be
//          animated independently of the scrim.
//   Scrim  the dimmed backdrop. pointer-events: none, so clicks fall through to
//          Root and a single `target === currentTarget` test covers the whole
//          backdrop, including any padding around the window.
//   Window the macOS window card. This is what scales.
//
// Layering: sits at 1000. Above the navbar (100) and the noise overlay (0),
// below the shared Loader (9999) and SuccessModal (10000), so sending a
// message from inside a modal still shows the spinner on top of everything.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

import { device } from '../../styles/breakpoints';

export const MODAL_Z_INDEX = 1000;

const sizeMap = {
  /** Forms and confirmations. */
  md: '560px',
  /** Wider content such as a project write-up. */
  lg: '720px',
} as const;

export type ModalBoxSize = keyof typeof sizeMap;

/**
 * The global focus rule in globalStyles.ts is `button:focus-visible` at
 * specificity (0,2,1). A single class selector cannot beat it, so the ring is
 * applied with `&&`.
 */
const focusRing = css`
  &&:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }
`;

/**
 * Guards rules that outrank the global reduced-motion block, so they have to be
 * repeated locally. Framer Motion handles the open/close transforms itself
 * through useReducedMotion; this covers the CSS hover micro-interactions.
 */
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

/* -------------------------------------------------------------------- root */

export const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${MODAL_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  /* Phones: the window becomes a bottom sheet rather than a smaller desktop
     card. Anchoring it to the bottom edge is what makes it read as a sheet
     instead of a shrunk dialog, and it keeps the title bar thumb-reachable. */
  @media ${device.tablet} {
    align-items: flex-end;
    padding: 0;
  }
`;

export const Scrim = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  /* Purely decorative: clicks belong to Root so one handler covers the whole
     backdrop. Without this the scrim would swallow the click and the
     target === currentTarget test in Root would never match. */
  pointer-events: none;
`;

/* ------------------------------------------------------------------ window */

export const Window = styled(motion.div)<{ $size: ModalBoxSize }>`
  position: relative;
  display: flex;
  flex-direction: column;
  /* The single most important rule in this file: the title bar stays put and
     only the body scrolls, so the close control is always reachable. */
  overflow: hidden;
  width: 100%;
  max-width: ${({ $size }) => sizeMap[$size]};
  max-height: 88vh;
  max-height: 88dvh;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.borderColor};
  /* theme.borderRadius.lg is 12px. A window with very round corners stops
     reading as a window, so the radius is held down deliberately. */
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  @media ${device.tablet} {
    max-width: 100%;
    /* Sheet geometry: square against the bottom of the screen, rounded only at
       the top. 100dvh tracks the collapsing mobile browser chrome; the plain
       100vh line above it is the fallback for browsers without it. */
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: ${({ theme }) => theme.borderRadius.lg}
      ${({ theme }) => theme.borderRadius.lg} 0 0;
    border-bottom: 0;
  }

  @media ${device.mobile} {
    max-height: 94vh;
    max-height: 94dvh;
  }
`;

/* --------------------------------------------------------------- title bar */

export const TitleBar = styled.header`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  padding: 0.85rem 1.1rem;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  /* A title bar is dragged, not selected. */
  user-select: none;
  -webkit-user-select: none;

  @media ${device.mobile} {
    padding: 0.75rem 1rem;
  }
`;

export const TrafficLights = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

/**
 * macOS system colours for the window controls. These are deliberately hardcoded
 * rather than themed: they are operating-system chrome, and a "themed" red
 * button would stop reading as close. The inner ring is what gives the dots
 * their macOS outline against the dark surface.
 */
const lightBase = css`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.14);
  flex-shrink: 0;
`;

/**
 * The only interactive control in the group. Yellow and green are decorative
 * (see DecorativeLight) rather than wired to fake minimise/maximise behaviour.
 */
export const CloseLight = styled.button`
  ${lightBase};
  padding: 0;
  border: 0;
  background: #ff5f57;
  cursor: pointer;
  /* globalStyles.ts lifts and shadows every button on hover. A 12px dot must
     not jump, so the global hover is undone here. */
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.08);
    transform: none;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.14);
  }

  &:active {
    transform: none;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.14);
  }

  ${focusRing}
  ${noMotion}
`;

/**
 * Yellow and green dots. Presentational only: rendering them as buttons would
 * put two dead controls in the tab order, which is worse than not having them.
 */
export const DecorativeLight = styled.span<{ $color: 'yellow' | 'green' }>`
  ${lightBase};
  background: ${({ $color }) => ($color === 'yellow' ? '#febc2e' : '#28c840')};
`;

export const WindowTitle = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${({ theme }) => theme.text};
  /* Long titles truncate rather than pushing the traffic lights off-centre. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${device.mobile} {
    font-size: 0.8125rem;
  }
`;

/* -------------------------------------------------------------------- body */

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.75rem 1.5rem;
  overflow-y: auto;
  /* Stops a flick that reaches the end of the content from scrolling the page
     behind the modal. */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  @media ${device.tablet} {
    padding: 1.5rem 1.25rem;
  }

  @media ${device.mobile} {
    padding: 1.25rem 1rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }

  /* Scrollbar, matched to the rest of the site. */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.accent};
    border-radius: 9999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.primary};
  }
`;

export const Footer = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  background: ${({ theme }) => theme.background};
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  @media ${device.mobile} {
    padding: 0.875rem 1rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
  }
`;
